export interface LoanPayment {
  month: number;
  principalPayment: number;
  interestPayment: number;
  totalPayment: number;
  remainingBalance: number;
}

export interface LoanCalculationParams {
  loanAmount: number;
  months: number;
  annualInterestRate: number;
  gracePeriod: number;
}

/**
 * מחשב החזרי הלוואה עם תמיכה בגרייס
 * שיטת החישוב: תשלומים שווים (שפיצר)
 */
export function calculateLoanPayments(params: LoanCalculationParams): LoanPayment[] {
  const { loanAmount, months, annualInterestRate, gracePeriod } = params;
  const monthlyInterestRate = annualInterestRate / 100 / 12;
  const payments: LoanPayment[] = [];
  let remainingBalance = loanAmount;

  // בדיקה אם זו הלוואת בלון (גרייס מלא)
  const isBalloonLoan = gracePeriod === months;

  if (isBalloonLoan) {
    // הלוואת בלון - רק ריבית בכל החודשים, והקרן בחודש האחרון
    for (let month = 1; month < months; month++) {
      const interestPayment = remainingBalance * monthlyInterestRate;
      payments.push({
        month,
        principalPayment: 0,
        interestPayment,
        totalPayment: interestPayment,
        remainingBalance,
      });
    }

    // החודש האחרון - ריבית + כל הקרן
    const finalInterestPayment = remainingBalance * monthlyInterestRate;
    payments.push({
      month: months,
      principalPayment: remainingBalance,
      interestPayment: finalInterestPayment,
      totalPayment: remainingBalance + finalInterestPayment,
      remainingBalance: 0,
    });
  } else {
    // תקופת גרייס חלקית - רק תשלום ריבית
    for (let month = 1; month <= gracePeriod; month++) {
      const interestPayment = remainingBalance * monthlyInterestRate;
      payments.push({
        month,
        principalPayment: 0,
        interestPayment,
        totalPayment: interestPayment,
        remainingBalance,
      });
    }

    // תקופת החזר רגילה - תשלומים שווים (שפיצר)
    const remainingMonths = months - gracePeriod;

    if (remainingMonths > 0) {
      // החזרים חודשיים שווים
      const monthlyPayment = calculateEqualMonthlyPayment(
        remainingBalance,
        monthlyInterestRate,
        remainingMonths
      );

      for (let month = gracePeriod + 1; month <= months; month++) {
        const interestPayment = remainingBalance * monthlyInterestRate;
        const principalPayment = monthlyPayment - interestPayment;
        remainingBalance -= principalPayment;

        payments.push({
          month,
          principalPayment,
          interestPayment,
          totalPayment: monthlyPayment,
          remainingBalance: Math.max(0, remainingBalance),
        });
      }
    }
  }

  return payments;
}

/**
 * מחשב תשלום חודשי קבוע (PMT) לפי נוסחת הלוואה
 */
function calculateEqualMonthlyPayment(
  principal: number,
  monthlyRate: number,
  months: number
): number {
  if (monthlyRate === 0) {
    return principal / months;
  }

  return (
    (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
    (Math.pow(1 + monthlyRate, months) - 1)
  );
}
